import {
  HttpMethod,
  HeadersType,
  Milliseconds,
  Header,
  IExternalRequester,
  SendRequestParametersType,
  Parser,
  RequestType,
  KameleoonResponseType,
  KameleoonUtils,
  KameleoonLogger,
} from '@kameleoon/javascript-sdk-core';
import { RequestParametersType, CredentialsType } from './types';
import { ACCESS_TOKEN_URL } from './constants';

// --- Note ---
// `RequesterMiddleware` is a decorator for `Requester` class to add
// authorization layer to Node.js SDK
export class RequesterMiddleware implements IExternalRequester {
  private requester: IExternalRequester;
  private credentials: CredentialsType;
  private nextAccessTokenUpdate?: number | null;
  private accessToken?: string | null;
  private _isKameleoonRequester = false;

  constructor(requester: IExternalRequester, credentials: CredentialsType) {
    this.requester = requester;
    this.credentials = credentials;
    this._isKameleoonRequester = 'isKameleoonRequester' in requester;
  }

  get isKameleoonRequester(): boolean {
    return this._isKameleoonRequester;
  }

  public async sendRequest<T extends RequestType>({
    url,
    parameters,
    retryCount,
    requestType,
  }: SendRequestParametersType<T>): Promise<KameleoonResponseType> {
    let headers: HeadersType | undefined;
    let updatedParameters = parameters;
    const needAuth =
      requestType === RequestType.Tracking ||
      requestType === RequestType.RemoteData;

    if (needAuth) {
      let hasGoodToken = !this.shouldUpdateAccessToken();

      if (!hasGoodToken) {
        hasGoodToken = await this.fetchAccessToken(parameters.signal);
      }

      if (hasGoodToken) {
        headers = this.getAuthHeader(parameters);
        updatedParameters = { ...parameters, headers };
      }
    }

    if (requestType === RequestType.Tracking) {
      let response = await this.requester.sendRequest({
        url,
        parameters: updatedParameters,
        retryCount,
        requestType,
      });

      if (response.status === 401) {
        // --- Note ---
        // Auth error - retry with new token
        this.nextAccessTokenUpdate = null;
        this.accessToken = null;

        // --- Note ---
        // If this is the last retry with `401` status
        // we try once more without authorization
        if (retryCount === 2) {
          response = await this.requester.sendRequest({
            url,
            parameters,
            requestType,
            retryCount,
          });
        }

        return response;
      }

      // --- Note ---
      // Forbidden (bot filtering) - simulate successful response from utils
      // to indicate that the bot was filtered
      if (response.status === 403) {
        return KameleoonUtils.simulateSuccessRequest<RequestType.Tracking>(
          requestType,
          null,
        );
      }

      return response;
    }

    return await this.requester.sendRequest({
      url,
      parameters,
      requestType,
      retryCount,
    });
  }

  private async fetchAccessToken(signal: AbortSignal | null): Promise<boolean> {
    const { clientId, clientSecret } = this.credentials;
    const headers = {
      [Header.ContentType]: 'application/x-www-form-urlencoded',
    };

    KameleoonLogger.debug(() => {
      return (
        `Running access token request Method: ${HttpMethod.Post}, Url: ${ACCESS_TOKEN_URL}, Headers: ${JSON.stringify(headers)},` +
        ` with clientId: ${Parser.parseSecret(
          clientId,
        )} and clientSecret: ${Parser.parseSecret(clientSecret)}`
      );
    });
    const parameters = {
      method: HttpMethod.Post,
      signal,
      headers,
      body: `grant_type=client_credentials&client_id=${clientId}&client_secret=${clientSecret}`,
    } as const;

    const response = await this.requester.sendRequest({
      url: ACCESS_TOKEN_URL,
      requestType: RequestType.Tracking,
      parameters,
    });

    KameleoonLogger.debug(() => {
      return (
        `Received access token response: status: ${response.status} for request Method: ${HttpMethod.Post}, ` +
        `Url: ${ACCESS_TOKEN_URL}, Headers: ${JSON.stringify(headers)}, ` +
        `with clientId: ${Parser.parseSecret(
          clientId,
        )} and clientSecret: ${Parser.parseSecret(clientSecret)}`
      );
    });
    if (response.ok) {
      const json = await response.json();

      if (typeof json?.access_token === 'string') {
        this.accessToken = json.access_token;

        this.nextAccessTokenUpdate =
          new Date().getTime() + 1.8 * Milliseconds.Hour;
      }

      return true;
    }

    return false;
  }

  private shouldUpdateAccessToken(): boolean {
    if (!this.nextAccessTokenUpdate) {
      return true;
    }

    const now = new Date().getTime();

    return now > this.nextAccessTokenUpdate;
  }

  private getAuthHeader(
    parameters: RequestParametersType,
  ): HeadersType | undefined {
    if (!('headers' in parameters)) {
      return { [Header.Authorization]: `Bearer ${this.accessToken}` };
    }

    const headers = parameters.headers as HeadersType;

    if (!this.accessToken) {
      return headers;
    }

    return {
      ...headers,
      [Header.Authorization]: `Bearer ${this.accessToken}`,
    };
  }
}
